import { Box } from '@material-ui/core';
import { UserType } from '@nemuru-eng/js-api-client';

import { ROLE_ICON_NAMES } from '@/components/shared/components/AccountButton';
import { Icon } from '@/components/shared/components/Icon';
import { IconHeaderCard } from '@/components/shared/components/IconHeaderCard';
import { APP_ROLES } from '@/config/config';
import { UserEntity } from '@/entities/UserEntity';
import UserInMerchantStructure from '@/entities/UserInMerchantStructure';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { getIconName } from '@/utils/getIconName';

interface OrganizationProps {
  userEntity: UserEntity | UserInMerchantStructure;
}

export const Organization = ({ userEntity }: OrganizationProps) => {
  const { palette } = useCustomTheme();
  const { sm } = useScreen();

  if (!userEntity.isRoleMerchantChannelOrAgent) {
    return <UserCard userType={userEntity.user.userType} userEntity={userEntity} />;
  }

  const cardNumber = userEntity.hasUserType([UserType.AGENT]) ? 3 : userEntity.hasUserType([UserType.CHANNEL]) ? 2 : 1;

  return (
    <Box
      display={sm ? 'grid' : 'flex'}
      gridTemplateColumns={`repeat(${cardNumber}, 1fr)`}
      flexDirection={sm ? 'row' : 'column'}
      alignItems={'center'}
      width="100%"
      style={{ color: palette.text.secondary }}
    >
      <Box
        display="flex"
        flexDirection={sm ? 'row' : 'column'}
        alignItems={'center'}
        alignSelf="stretch"
        height={sm ? '80px' : 'fit-content'}
        maxHeight={sm ? '80px' : 'fit-content'}
        width="100%"
      >
        <UserCard userType={UserType.MERCHANT} userEntity={userEntity} />
      </Box>
      {userEntity.hasUserType([UserType.CHANNEL, UserType.AGENT]) && (
        <Box
          display="flex"
          flexDirection={sm ? 'row' : 'column'}
          alignItems={'center'}
          alignSelf="stretch"
          height={sm ? '80px' : 'fit-content'}
          maxHeight={sm ? '80px' : 'fit-content'}
          width="100%"
        >
          <Box display="flex" alignItems={'center'} justifyContent={'center'} height={32} width={32} flexShrink={0}>
            <Icon size={16} style={{ transform: sm ? 'none' : 'rotate(90deg)', height: 16, width: 16 }}>
              chevron_right
            </Icon>
          </Box>
          <UserCard userType={UserType.CHANNEL} userEntity={userEntity} />
        </Box>
      )}
      {userEntity.hasUserType([UserType.AGENT]) && (
        <Box
          display="flex"
          flexDirection={sm ? 'row' : 'column'}
          alignItems={'center'}
          alignSelf="stretch"
          height={sm ? '80px' : 'fit-content'}
          maxHeight={sm ? '80px' : 'fit-content'}
          width="100%"
        >
          <Box display="flex" alignItems={'center'} justifyContent={'center'} height={32} width={32}>
            <Icon size={16} style={{ transform: sm ? 'none' : 'rotate(90deg)', height: 16, width: 16 }}>
              chevron_right
            </Icon>
          </Box>
          <UserCard userType={UserType.AGENT} userEntity={userEntity} />
        </Box>
      )}
    </Box>
  );
};

interface UserCardProps {
  userType: UserType;
  userEntity: UserEntity | UserInMerchantStructure;
}

const UserCard = ({ userType, userEntity }: UserCardProps) => {
  const { palette } = useCustomTheme();

  const { name, iconName } = getNameAndIcon({ userType, userEntity });

  return (
    <IconHeaderCard
      surLabelTid={userType.toUpperCase()}
      labelTid={name}
      iconName={iconName}
      color={'secondary'}
      paperProps={{
        elevation: 0,
        style: {
          backgroundColor: palette.grey[100],
          height: '100%',
          width: '100%',
          minWidth: 0,
          paddingBlock: 4,
          margin: 0,
        },
      }}
    />
  );
};

export const getNameAndIcon = (data: UserCardProps) => {
  const { userType, userEntity } = data;

  let name: string;
  let iconName: string;

  if (!userEntity.isRoleMerchantChannelOrAgent()) {
    return { name: userEntity.user.username, iconName: ROLE_ICON_NAMES[APP_ROLES.SUPER_ADMIN] };
  }

  if (userType === UserType.AGENT) {
    name = userEntity.agent!.name;
    iconName = ROLE_ICON_NAMES[APP_ROLES.AGENT];
  } else if (userType === UserType.CHANNEL) {
    const merchant = userEntity.merchant!;
    const channel = userEntity.channel!;
    name = channel.description;
    iconName = getIconName(merchant.channels[0].type);
  } else if (userType === UserType.MERCHANT) {
    const merchant = userEntity.merchant!;
    name = merchant.businessName;
    iconName = ROLE_ICON_NAMES[APP_ROLES.MERCHANT];
  } else {
    name = userEntity.user.username;
    iconName = ROLE_ICON_NAMES[APP_ROLES.SUPER_ADMIN];
  }

  return { name, iconName };
};
